import { useEffect, useRef } from 'react';

import { usePartnerDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import { Partner, PartnerApi, partnerList, PartnerSortField, PartnerTypes } from 'apis/PartnerApi';
import { NewPartnerForm } from 'pages/partners/components/NewPartnerForm/NewPartnerForm';
import { PartnerGMetrixTable } from 'components/PartnerGMetrixTable/PartnerGMetrixTable';
import { DEFAULT_PARTNER } from 'pages/partners/components/PartnerForm/constants';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { currentLookupUrl, getStringFromURL } from 'utils/other';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Col, Modal, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePartners } from 'pages/partners/context';
import { useSuperState, useUrlFilters } from 'hooks';
import { useNotification } from 'app/notifications';
import { PageWithHeader } from 'components/layout';
import { helpContent } from 'pages/partners/help';
import { SortDescriptor } from 'utils/dataQuery';
import { KendoIntlProvider } from 'app/culture';
import { PageTitle } from 'utils/pageTitle';

import 'pages/partners/styles/main.scss';
import 'styles/global-styles.scss';

export const Partners = () => {

    // Persist the view (search, sort, pagination) in the URL query string so navigating away
    // and back restores the same view, then refetch fresh partner rows. Partner type stays in
    // the URL as its own `PType` param.
    const urlFilters = useUrlFilters({
        searchterm: '',
        skip: 0,
        sortdir: '',
        sortfield: '',
        take: 15
    });

    const { partner, permissions } = usePartners();
    const { search } = useLocation();
    const urlPartnerType = (new URLSearchParams(search).get('PType') ?? new URLSearchParams(search).get('ptype'));
    const parsedUrlPartnerType = urlPartnerType ? Number(urlPartnerType) : null;
    const activePartnerType = (
        (Number.isFinite(parsedUrlPartnerType)
            ? parsedUrlPartnerType
            : partner.type) as PartnerTypes | null
    );

    // /partners is shared across partner types (see the PType effect below), so the widget is
    // keyed by the active type only through that effect resetting state.
    const [state, setState] = useSuperState({
        loadingGridData: true,
        paginationSkip: urlFilters.initial.skip,
        paginationTake: urlFilters.initial.take,
        partners: [] as Partner[],
        searchTerm: urlFilters.initial.searchterm,
        selectedPartners: [] as Partner[],
        serverTotalCount: 0,
        showNewPartnerModal: false,
        sortDir: urlFilters.initial.sortdir,
        sortField: urlFilters.initial.sortfield as PartnerSortField | ''
    });
    const { loadingGridData, paginationSkip, paginationTake, partners, searchTerm, selectedPartners, serverTotalCount, showNewPartnerModal, sortDir, sortField } = state;

    const { formatMessage, locale } = useIntl();
    const partnerDefaultLookupSearchFields = usePartnerDefaultLookupSearchFields();
    const { createToast } = useNotification();
    const navigate = useNavigate();
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInitialSearchRef = useRef(true);
    const lastPTypeRef = useRef<PartnerTypes | null>(null);

    useEffect(() => {

        // Always refetch on mount, restoring the previous page/sort/search for this partner type.
        fetchPartnersPaged(paginationSkip, paginationTake, sortField, sortDir, searchTerm);

        // this means create mode
        if (getStringFromURL('create') === 'true') {

            setState({ showNewPartnerModal: true });

        }

    }, []);

    // Debounce search term changes for server-side pagination (1s delay)
    useEffect(() => {

        // Skip the first render so we don't double-fetch on mount
        if (isInitialSearchRef.current) {

            isInitialSearchRef.current = false;
            return;

        }

        if (searchDebounceRef.current) {

            clearTimeout(searchDebounceRef.current);

        }

        setState({ loadingGridData: true });

        searchDebounceRef.current = setTimeout(() => {

            setState({ paginationSkip: 0 });
            fetchPartnersPaged(0, paginationTake, sortField, sortDir, searchTerm);

        }, 1000);

        return () => {

            if (searchDebounceRef.current) {

                clearTimeout(searchDebounceRef.current);

            }

        };

    }, [searchTerm]);

    // The /partners route is reused across partner types, so React may keep
    // this component mounted during navigation. React to PType changes by
    // resetting grid state and re-fetching the correct partner-type data.
    useEffect(() => {

        if (lastPTypeRef.current === null) {

            lastPTypeRef.current = activePartnerType;
            return;

        }

        if (lastPTypeRef.current !== activePartnerType) {

            setState({
                paginationSkip: 0,
                searchTerm: '',
                selectedPartners: [],
                sortDir: '',
                sortField: ''
            });
            fetchPartnersPaged(0, paginationTake, '', '', '', activePartnerType ?? 4);

        }

        lastPTypeRef.current = activePartnerType;

    }, [activePartnerType]);

    const fetchPartnersPaged = async (
        skip = paginationSkip,
        take = paginationTake,
        sField = sortField,
        sDir = sortDir,
        search = searchTerm,
        pType = activePartnerType ?? 4
    ) => {

        setState({ loadingGridData: true });
        const page = Math.floor(skip / take) + 1;

        try {

            const result = await PartnerApi.GetAllPaged(
                pType,
                page,
                take,
                sField,
                sDir,
                search
            );
            result.Items.forEach(p => {

                p.Id = p.ID;

            });
            setState({ partners: result.Items, serverTotalCount: result.TotalCount });
            urlFilters.write({
                searchterm: search,
                skip,
                sortdir: sDir,
                sortfield: sField,
                take
            });

        } catch (e) {

            console.error(e);
            const message = typeof e === 'string' ? e : (e as any)?.message || (e as any)?.Message || '';
            if (message.toLowerCase().includes('timed out') || message.toLowerCase().includes('timeout')) {

                createToast(formatMessage({ id: 'the_request_timed_out_please_try_again.93e77e8d', defaultMessage: 'The request timed out. Please try again.' }), 'danger');

            } else {

                createToast(formatMessage({ id: 'failed_to_load_partner_data_please_try_a.52e9f977', defaultMessage: 'Failed to load partner data. Please try again.' }), 'danger');

            }

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const handleServerPageChange = (skip: number, take: number) => {

        setState({ paginationSkip: skip, paginationTake: take });
        fetchPartnersPaged(skip, take, sortField, sortDir, searchTerm);

    };

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const newSortField = (sort.length > 0 ? sort[0].field : '') as PartnerSortField | '';
        const newSortDir = sort.length > 0 ? (sort[0].dir ?? '') : '';
        setState({ paginationSkip: 0, sortDir: newSortDir, sortField: newSortField });
        fetchPartnersPaged(0, paginationTake, newSortField, newSortDir, searchTerm);

    };

    const getPartnerTypeName = () => {

        const indexOf = partnerList.findIndex(partnerItem => partnerItem.Id === activePartnerType);
        if (indexOf != -1) {

            return `${partnerList[indexOf].Name}`;

        } else {

            return formatMessage({ id: 'partner.cb022868', defaultMessage: 'Partner' });

        }

    };

    const createNewPartnerClick = async () => {

        setState({ showNewPartnerModal: true });

    };

    const reloadPartners = async () => {

        setState({ paginationSkip: 0 });
        fetchPartnersPaged(0, paginationTake, sortField, sortDir, searchTerm);

    };

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>
                    <PageTitle title={`${formatMessage({ id: 'partnertype_list.667de4d8', defaultMessage: '{partnerType} List' }, { partnerType: getPartnerTypeName() })} | GMetrix`} />
                    <PageWithHeader
                        headerConfig={{
                            cta: {
                                disabled: activePartnerType === null || !permissions.Edit || !permissions.Create,
                                label: (
                                    <div className={'d-inline-flex align-items-center gap-1'}>
                                        <i className={'fa-solid fa-plus'} />
                                        <span>
                                            <FormattedMessage defaultMessage="New" id="new.03d9347f" />
                                            {' '}
                                            {getPartnerTypeName()}
                                        </span>
                                    </div>
                                ),
                                onClick: createNewPartnerClick
                            },
                            helpContent: helpContent,
                            title: (
                                <>
                                    {getPartnerTypeName()}
                                    {' '}
                                    <FormattedMessage defaultMessage="List" id="list.d08426af" />
                                </>
                            )
                        }}
                    >
                        <div className="partners d-flex flex-column gap-3">

                            <Card body>
                                {activePartnerType === null && (
                                <Row className="mb-4">
                                    <Col lg={4} md={12} sm={12} xl={3} xs={12}>
                                        <GMetrixDropdown
                                            onChange={dataItem => {

                                                navigate(`/partners?PType=${dataItem.Id.toString()}`);

                                            }}
                                            dataItemKey="Id"
                                            dataItems={partnerList}
                                            filterable={false}
                                            label={formatMessage({ id: 'select_partner_type.430344a1', defaultMessage: 'Select Partner Type' })}
                                            textField="Name"
                                        />
                                    </Col>
                                </Row>
                                )}

                                <PartnerGMetrixTable
                                    serverSidePagination
                                    columnData={[
                                        { field: 'Id', sortable: false, title: formatMessage({ id: 'id.e9650a61', defaultMessage: 'ID' }) },
                                        { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                                        { field: 'ContactEmail', title: formatMessage({ id: 'contact_email.f9a375b0', defaultMessage: 'Contact Email' }) },
                                        {
                                            field: 'CustomerIDString',
                                            sortField: 'CustomerID',
                                            title: formatMessage({ id: 'customer_id.29f2845e', defaultMessage: 'Customer ID' })
                                        },
                                        { field: 'DateCreate', sortField: 'dateCreate', title: formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' }) }
                                    ]}
                                    advancedSearchFields={partnerDefaultLookupSearchFields}
                                    allowExport={false}
                                    defaultItemsPerPage={paginationTake}
                                    getPartnerTypeName={getPartnerTypeName}
                                    initialSkip={paginationSkip}
                                    loadingGridData={loadingGridData}
                                    multiselect={true}
                                    onDetailsItemClick={dataItem => navigate('/partners/' + dataItem.ID, { state: { from: currentLookupUrl() } })}
                                    onSelectedChange={val => setState({ selectedPartners: val })}
                                    onServerPageChange={handleServerPageChange}
                                    onServerSortChange={handleServerSortChange}
                                    partners={partners}
                                    searchTerm={searchTerm}
                                    selectableRows={false}
                                    selectedPartners={selectedPartners}
                                    serverTotalCount={serverTotalCount}
                                    setSearchTerm={val => setState({ searchTerm: val })}
                                    toggleColumns={true}
                                />
                            </Card>
                        </div>

                    </PageWithHeader>

                    <Modal onHide={() => setState({ showNewPartnerModal: false })} show={showNewPartnerModal} size="xl" >
                        <Modal.Header
                            closeButton
                        >
                            <Modal.Title className="w-100">
                                <FormattedMessage defaultMessage="New" id="new.03d9347f" />
                                {' '}
                                {getPartnerTypeName()}
                            </Modal.Title>
                        </Modal.Header>
                        <NewPartnerForm
                            details={
                                { ...DEFAULT_PARTNER, PartnerType: activePartnerType } as any

                            }
                            onSuccess={partner => {

                                setState({ showNewPartnerModal: false });
                                const url = new URL(window.location.href);
                                const search_params = url.searchParams;
                                search_params.delete('create');
                                url.search = search_params.toString();
                                window.history.replaceState(null, '', url);
                                reloadPartners();
                                navigate('/partners/' + partner.ID, { state: { from: currentLookupUrl() } });

                            }}
                            ActionsWrapper={Modal.Footer}
                            ContentWrapper={Modal.Body}
                            onClose={() => setState({ showNewPartnerModal: false })}
                        />

                    </Modal>

                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>
    );

};
